import type { DomainEvent, EventEnvelope, Order, OrderDetail } from '@multicrm/shared';
import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { eventStreamUrl } from './api';
import { patchOrderInLists, queryKeys } from './queries';
import { haptics } from './telegram';

export type ConnectionState = 'connecting' | 'live' | 'offline';

interface LiveContextValue {
  state: ConnectionState;
  lastSeq: number;
  /** последнее событие: экраны подсвечивают изменившуюся карточку */
  lastEvent: DomainEvent | null;
}

const LiveContext = createContext<LiveContextValue>({
  state: 'offline',
  lastSeq: 0,
  lastEvent: null,
});

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30_000;

/**
 * Живые обновления.
 *
 * Поток SSE от gateway: магазин меняется — список обновляется сам, без
 * перезагрузки и без опроса из приложения. Вместо инвалидации всего кэша
 * правим ровно то, что изменилось: так список не мигает и не прыгает.
 *
 * При обрыве переподключаемся с растущей паузой и передаём последний seq —
 * сервер досылает пропущенное.
 */
export function LiveProvider({
  enabled,
  onNewOrder,
  children,
}: {
  enabled: boolean;
  onNewOrder?: (event: Extract<DomainEvent, { type: 'order.created' }>) => void;
  children: ReactNode;
}): ReactNode {
  const client = useQueryClient();
  const [socketState, setSocketState] = useState<ConnectionState>('connecting');
  const [lastEvent, setLastEvent] = useState<DomainEvent | null>(null);
  const lastSeq = useRef(0);
  const [seq, setSeq] = useState(0);
  const newOrderHandler = useRef(onNewOrder);
  useEffect(() => {
    newOrderHandler.current = onNewOrder;
  }, [onNewOrder]);

  // Выключенный канал — не состояние соединения, а отсутствие попытки.
  const state: ConnectionState = enabled ? socketState : 'offline';

  const apply = useCallback(
    (event: DomainEvent) => {
      switch (event.type) {
        case 'order.created':
          void client.invalidateQueries({ queryKey: ['orders'] });
          void client.invalidateQueries({ queryKey: ['stats'] });
          haptics.success();
          newOrderHandler.current?.(event);
          break;

        case 'order.status_changed':
          patchOrderInLists(client, event.ref, (order) => ({ ...order, status: event.to }));
          client.setQueryData<OrderDetail>(queryKeys.order(event.ref), (current) =>
            current ? { ...current, status: event.to } : current,
          );
          void client.invalidateQueries({ queryKey: ['orders', 'count'] });
          void client.invalidateQueries({ queryKey: ['stats'] });
          break;

        case 'order.tracking_changed':
          patchOrderInLists(client, event.ref, (order) => ({
            ...order,
            delivery: { ...order.delivery, trackingStatus: event.to },
          }));
          void client.invalidateQueries({ queryKey: queryKeys.order(event.ref) });
          break;

        case 'order.payment_changed':
          void client.invalidateQueries({ queryKey: queryKeys.orderPayments(event.ref) });
          void client.invalidateQueries({ queryKey: queryKeys.order(event.ref) });
          break;

        case 'store.status_changed':
          void client.invalidateQueries({ queryKey: queryKeys.bootstrap });
          break;
      }
      setLastEvent(event);
    },
    [client],
  );

  useEffect(() => {
    if (!enabled) return;

    let source: EventSource | null = null;
    let reconnectTimer: number | undefined;
    let attempt = 0;
    let stopped = false;

    const scheduleReconnect = (): void => {
      if (stopped) return;
      const delay = Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_MAX_MS);
      attempt += 1;
      reconnectTimer = window.setTimeout(() => {
        setSocketState('connecting');
        void connect();
      }, delay);
    };

    const handleEnvelope = (raw: string): void => {
      try {
        const envelope = JSON.parse(raw) as EventEnvelope;
        if (envelope.seq <= lastSeq.current) return;
        lastSeq.current = envelope.seq;
        setSeq(envelope.seq);
        apply(envelope.event);
      } catch {
        // Битое сообщение не повод рвать поток.
      }
    };

    async function connect(): Promise<void> {
      if (stopped) return;

      try {
        const url = await eventStreamUrl(lastSeq.current);
        if (stopped) return;

        source = new EventSource(url);

        source.addEventListener('hello', () => {
          attempt = 0;
          setSocketState('live');
        });
        source.addEventListener('ping', () => setSocketState('live'));

        for (const type of [
          'order.created',
          'order.status_changed',
          'order.tracking_changed',
          'order.payment_changed',
          'store.status_changed',
        ]) {
          source.addEventListener(type, (message) =>
            handleEnvelope((message as MessageEvent<string>).data),
          );
        }

        source.onerror = () => {
          // EventSource переподключился бы сам, но тикет одноразовый —
          // соединение нужно поднимать заново, получив новый.
          source?.close();
          source = null;
          setSocketState('offline');
          scheduleReconnect();
        };
      } catch {
        setSocketState('offline');
        scheduleReconnect();
      }
    }

    void connect();

    // Мини-апп могли свернуть на час: при возврате сразу поднимаем поток.
    const onVisible = (): void => {
      if (document.visibilityState !== 'visible' || source) return;
      window.clearTimeout(reconnectTimer);
      attempt = 0;
      void connect();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      stopped = true;
      window.clearTimeout(reconnectTimer);
      document.removeEventListener('visibilitychange', onVisible);
      source?.close();
    };
  }, [enabled, apply]);

  const value = useMemo<LiveContextValue>(
    () => ({ state, lastSeq: seq, lastEvent }),
    [state, seq, lastEvent],
  );

  return <LiveContext.Provider value={value}>{children}</LiveContext.Provider>;
}

export function useLive(): LiveContextValue {
  return useContext(LiveContext);
}

/** Изменился ли этот заказ последним событием — для короткой подсветки карточки. */
export function useIsRecentlyChanged(order: Pick<Order, 'ref'>): boolean {
  const { lastEvent } = useLive();
  if (!lastEvent || !('ref' in lastEvent)) return false;
  return lastEvent.ref === order.ref;
}
